import { useState } from 'react'
import { toast } from 'sonner'
import { apiError, useAdminUsersQuery, useCreateUserMutation, useDeleteUserMutation, useMeQuery, useResetUserPasswordMutation } from '@/store/api'
import type { DashboardUserRow } from '@shared/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input, Label, FieldError } from '@/components/ui/input'
import { Table, THead, Th, Tr, Td, SkeletonRows } from '@/components/ui/data-table'
import { ErrorState } from '@/components/ui/empty-state'
import { TempPasswordDialog } from '@/components/panels/TempPasswordDialog'
import { fmtDate } from '@/lib/format'

/** Atarim staff accounts. Admins see every partner, so they are managed here and nowhere else. */
export function Admins() {
  const { data: me } = useMeQuery()
  const { data: users, isLoading, isError, refetch } = useAdminUsersQuery()
  const [resetPassword, { isLoading: resetting }] = useResetUserPasswordMutation()
  const [deleteUser, { isLoading: deleting }] = useDeleteUserMutation()
  const [createOpen, setCreateOpen] = useState(false)
  const [reveal, setReveal] = useState<{ email: string; password: string } | null>(null)
  const [confirmReset, setConfirmReset] = useState<DashboardUserRow | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<DashboardUserRow | null>(null)
  const admins = users?.filter((u) => u.role === 'atarim_admin')

  return (
    <>
      <PageHeader
        title="Atarim admins"
        description="Atarim staff with access to every partner, contract metadata and internal notes."
        actions={<Button onClick={() => setCreateOpen(true)}>Create admin</Button>}
      />
      {isError ? (
        <ErrorState onRetry={refetch} />
      ) : (
        <Table>
          <THead>
            <Th>Email</Th>
            <Th>Created</Th>
            <Th right>Actions</Th>
          </THead>
          {isLoading ? (
            <SkeletonRows cols={3} rows={3} />
          ) : (
            <tbody>
              {admins?.map((u) => (
                <Tr key={u.id}>
                  <Td className="font-medium">
                    {u.email}
                    {u.id === me?.id && <span className="ml-2 text-xs font-normal text-muted-foreground">you</span>}
                    {u.mustChangePassword && <span className="ml-2 text-xs font-normal text-ata-orange">password not set yet</span>}
                  </Td>
                  <Td>{fmtDate(u.createdAt)}</Td>
                  <Td right>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" size="sm" onClick={() => setConfirmReset(u)}>
                        Reset password
                      </Button>
                      {u.id !== me?.id && (
                        <Button variant="ghost" size="sm" className="text-ata-red hover:bg-ata-red/10" onClick={() => setConfirmRemove(u)}>
                          Remove
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          )}
        </Table>
      )}

      <ConfirmDialog
        open={!!confirmReset}
        onOpenChange={(o) => !o && setConfirmReset(null)}
        title={`Reset the password for ${confirmReset?.email}?`}
        confirmLabel="Reset password"
        loading={resetting}
        onConfirm={async () => {
          const u = confirmReset!
          try {
            const res = await resetPassword(u.id).unwrap()
            setConfirmReset(null)
            setReveal({ email: u.email, password: res.tempPassword })
          } catch (err) {
            toast.error(apiError(err))
          }
        }}
      >
        <p>They are signed out everywhere immediately and must set a new password using the temporary one you'll be shown next.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={`Remove ${confirmRemove?.email}?`}
        confirmLabel="Remove admin"
        loading={deleting}
        onConfirm={async () => {
          const u = confirmRemove!
          try {
            await deleteUser(u.id).unwrap()
            toast.success(`${u.email} removed`)
            setConfirmRemove(null)
          } catch (err) {
            toast.error(apiError(err))
          }
        }}
      >
        <p>Their account is deleted and they are signed out. The activity log keeps what they changed.</p>
      </ConfirmDialog>

      <CreateAdminDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(email, password) => setReveal({ email, password })} />
      <TempPasswordDialog reveal={reveal} onClose={() => setReveal(null)} />
    </>
  )
}

function CreateAdminDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  onCreated: (email: string, tempPassword: string) => void
}) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [create, { isLoading }] = useCreateUserMutation()

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const res = await create({ email, role: 'atarim_admin' }).unwrap()
      onOpenChange(false)
      setEmail('')
      onCreated(res.user.email, res.tempPassword)
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Create admin" description="Admins see every partner, including contract details and internal notes.">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="a-email">Email</Label>
            <Input id="a-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="w-full" loading={isLoading}>
            Create admin
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}
